import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import caseStudies from '../data/caseStudies';
import imgBadgeScan from '../images/badge-scan-flow.png';
import imgReportingPie from '../images/reporting-pie-charts.png';
import imgReportingFull from '../images/reporting-full-dashboard.png';
import imgDashboardGauges from '../images/dashboard-gauges.png';
import imgDashboardGoals from '../images/dashboard-goals.png';
import imgSmsOutreach from '../images/sms-outreach.png';
import imgHeroBooth from '../images/event-activation-booth.jpg';
import imgQrKiosk from '../images/event-qr-kiosk.jpg';
import imgAmbassador from '../images/event-brand-ambassador.jpg';
import imgSweepstakes from '../images/microsite-sweepstakes.jpg';
import imgMultilingual from '../images/microsite-multilingual.jpg';
import imgBadgeScanIpad from '../images/ios-badge-scan-ipad.jpg';

/* Single source of truth for the eight capabilities: each one supplies both
   its selector card and the label on its detail panel. */
const capabilityCards = [
  {
    id: 'lead-collection',
    label: 'Lead Collection',
    desc: 'Mobile-optimized, branded registration workflows with branching logic and multilingual support.',
  },
  {
    id: 'touchpoints',
    label: 'Multiple Touchpoints',
    desc: 'QR codes, digital kiosks, and brand ambassador interactions — online and offline.',
  },
  {
    id: 'badge-scanning',
    label: 'Badge Scanning',
    desc: 'Instant low-touch data capture from event badges with integrations for all major platforms.',
  },
  {
    id: 'seamless-experience',
    label: 'Seamless Experience',
    desc: 'Register at one activation and get looked up instantly at the next, with no form to fill in twice.',
  },
  {
    id: 'reporting',
    label: 'Smarter Reporting',
    desc: 'Track full user journeys, interaction sources, and unique vs. repeat engagement.',
  },
  {
    id: 'dashboards',
    label: 'Live Dashboards',
    desc: 'Real-time registrant counts, opt-in rates, survey breakdowns, and team performance.',
  },
  {
    id: 'integrations',
    label: 'Custom Integrations',
    desc: 'API connections, CRM sync, and personalized triggered email or SMS outreach.',
  },
  {
    id: 'ios-app',
    label: 'iOS Native App',
    desc: 'On/offline data capture, multi-language support, and real-time team leaderboards.',
  },
];

const lifecycle = [
  {
    label: 'Pre-event Engagement',
    desc: 'Registration opens before anyone arrives on site.',
  },
  {
    label: 'Brand Interaction',
    desc: 'QR codes, kiosks and ambassadors meet people where they are.',
  },
  {
    label: 'On-site Data Collection',
    desc: 'Capture works the same online, offline and mid-crowd.',
  },
  {
    label: 'Customer Feedback',
    desc: 'Pre- and post-surveys sized to the moment.',
  },
  {
    label: 'Systems Integrations',
    desc: 'Data lands in your CRM, your API, your format.',
  },
  {
    label: 'Follow-Up from Survey Responses',
    desc: "Emails and texts built from each customer's own answers.",
  },
  {
    label: 'Analytics',
    desc: 'Journeys, sources, unique vs. repeat engagement.',
  },
  {
    label: 'ROI Measurement',
    desc: 'What the activation was worth, not just what it counted.',
  },
];

function Home() {
  const [activeTab, setActiveTab] = useState('lead-collection');

  const activeIndex = capabilityCards.findIndex((c) => c.id === activeTab);
  const activeCard = capabilityCards[activeIndex];

  const panelRef = useRef(null);
  // Which tab the panel was last scrolled for. Comparing against the value —
  // rather than tripping a "first render" boolean — means a mount never
  // scrolls, and an effect that runs twice for one change still scrolls once.
  // StrictMode replays effects in development, and a boolean guard is already
  // spent by the replay, which sent every arrival on this page down to the
  // panel.
  const lastScrolledTab = useRef(activeTab);
  // Set when the pick came from the dots at the foot of the panel. The reader
  // is at the bottom of a panel they have finished, so the new one has to be
  // taken from its top rather than left scrolled to its last inch.
  const returnToPanelTop = useRef(false);

  // The picker is tall enough that the panel can sit below the fold, so a
  // click would change something the reader cannot see. `block: 'nearest'`
  // scrolls only when the panel is actually out of view, and skips the
  // initial render so the page doesn't jump on load. `html` carries a
  // scroll-padding-top, so 'start' clears the sticky nav on its own.
  useEffect(() => {
    if (lastScrolledTab.current === activeTab) return;
    lastScrolledTab.current = activeTab;

    panelRef.current?.scrollIntoView({
      block: returnToPanelTop.current ? 'start' : 'nearest',
      behavior: 'smooth',
    });
    returnToPanelTop.current = false;
  }, [activeTab]);

  const selectFromDots = (id) => {
    if (id === activeTab) return;
    returnToPanelTop.current = true;
    setActiveTab(id);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Hero */}
      <section id="hero" className="hero hero--home">
        <div className="hero-media" aria-hidden="true">
          <img src={imgHeroBooth} alt="" />
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Event Data Capture & Lead Collection</h1>
          <p className="hero-subtitle">
            A team with 25+ years of combined experience powering smarter
            activations — from badge scanning to live dashboards to
            personalized follow-up.
          </p>
          <div className="hero-actions">
            <Link to="/work" className="cta-button">
              See our work
            </Link>
            <button
              onClick={() => scrollToSection('capabilities')}
              className="ghost-button"
            >
              What we do
            </button>
          </div>
        </div>
      </section>

      {/* Capabilities: the cards are the selector, and the panel below shows
          whichever one is picked. One list of eight, not two. */}
      <section id="capabilities" className="capabilities">
        <div className="container">
          <h2 className="section-title">
            Everything You Need to Capture, Connect &amp; Convert
          </h2>
          <div className="capability-explorer">
            <div
              className="capability-picker"
              role="tablist"
              aria-label="Capabilities"
            >
              {capabilityCards.map((cap) => {
                const selected = activeTab === cap.id;
                return (
                  <button
                    key={cap.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    className={`capability-card ${selected ? 'active' : ''}`}
                    onClick={() => setActiveTab(cap.id)}
                  >
                    <h3 className="capability-card-label">{cap.label}</h3>
                    <p className="capability-card-desc">{cap.desc}</p>
                    <span className="card-cta">
                      {selected ? 'Showing below ↓' : 'Explore →'}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="tab-content" ref={panelRef}>
              {/* Repeats the selected card's number and label verbatim, so the
                  panel is legible as that card opened up. */}
              <div className="panel-head">
                <span className="panel-head-num" aria-hidden="true">
                  {String(activeIndex + 1).padStart(2, '0')}
                </span>
                <h3 className="panel-head-title">{activeCard.label}</h3>
              </div>

              {/* Lead Collection */}
              {activeTab === 'lead-collection' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Our microsites are mobile optimized, totally customizable,
                    and deliver real-time insights.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Custom Branded Registration Workflows</h4>
                      <p>
                        Mobile-optimized, fully customizable registration
                        experiences that reflect your brand.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Personalized Question Sets</h4>
                      <p>
                        Custom question sets with branching logic to capture the
                        most relevant data.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Multilingual Features</h4>
                      <p>
                        Automatically detects user's browser language for
                        seamless global events.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Easy Input</h4>
                      <p>
                        Address autocomplete, browser autofill, and email
                        validation for frictionless data entry.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Seamless User Experience</h4>
                      <p>
                        Pre-registration online or in person, with easy check-in
                        and check-out processes.
                      </p>
                    </div>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>Real Registration Flows</h4>
                    <p className="screenshot-caption">
                      Fully branded to the client, in the attendee's own
                      language, with address autocomplete doing the typing.
                    </p>
                    <div className="screenshot-grid">
                      <div className="screenshot-frame">
                        <img
                          src={imgSweepstakes}
                          alt="Ford and LAFC branded sweepstakes registration form"
                          loading="lazy"
                        />
                      </div>
                      <div className="screenshot-frame">
                        <img
                          src={imgMultilingual}
                          alt="Spanish-language registration form with Google address autocomplete"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Multiple Touchpoints */}
              {activeTab === 'touchpoints' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Whether scanning a QR code, chatting with a brand
                    ambassador, or using a digital kiosk, customers can easily
                    connect with your brand, on or offline.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>QR Code Scanning</h4>
                      <p>
                        Quick and easy mobile registration via QR codes placed
                        throughout your event.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Brand Ambassador Interactions</h4>
                      <p>
                        Empower your team to capture leads directly through
                        face-to-face conversations.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Digital Kiosks</h4>
                      <p>
                        Self-service registration stations for high-traffic
                        areas and autonomous engagement.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Online & Offline</h4>
                      <p>
                        Seamlessly works both online and offline, syncing data
                        when connectivity returns.
                      </p>
                    </div>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>On the Floor</h4>
                    <p className="screenshot-caption">
                      The same capture flow, reached three different ways: a
                      kiosk, an ambassador's tablet, and a branded stand.
                    </p>
                    <div className="photo-grid">
                      <figure className="photo-frame">
                        <img
                          src={imgQrKiosk}
                          alt="Attendee scanning a QR code at a self-service kiosk"
                          loading="lazy"
                        />
                        <figcaption>Self-service QR kiosk</figcaption>
                      </figure>
                      <figure className="photo-frame">
                        <img
                          src={imgAmbassador}
                          alt="Brand ambassador capturing a lead on a tablet"
                          loading="lazy"
                        />
                        <figcaption>Brand ambassador capture</figcaption>
                      </figure>
                      <figure className="photo-frame">
                        <img
                          src={imgHeroBooth}
                          alt="Branded activation booth on the show floor"
                          loading="lazy"
                        />
                        <figcaption>Branded activation stand</figcaption>
                      </figure>
                    </div>
                  </div>
                </div>
              )}

              {/* Badge Scanning */}
              {activeTab === 'badge-scanning' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Integrated into customizable lead capture flow for low-touch
                    data collection during commercial events.
                  </p>
                  <div className="integrations-section">
                    <h4>Existing Integrations</h4>
                    <div className="integration-tags">
                      <span className="integration-tag">BadgeGuys</span>
                      <span className="integration-tag">CompuSystems</span>
                      <span className="integration-tag">Conexys</span>
                      <span className="integration-tag">Cvent</span>
                      <span className="integration-tag">LeadsBadgeScan</span>
                      <span className="integration-tag">Maritz</span>
                      <span className="integration-tag">VoiceHive</span>
                      <span className="integration-tag">...and more!</span>
                    </div>
                  </div>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Quick Data Capture</h4>
                      <p>
                        Instantly capture attendee information from event
                        badges.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Seamless Integration</h4>
                      <p>
                        Works with major event management platforms and badge
                        providers.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Low-Touch Collection</h4>
                      <p>
                        Minimize friction while maximizing data quality at
                        commercial events.
                      </p>
                    </div>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>See It In Action</h4>
                    <p className="screenshot-caption">
                      Scan a badge QR code and watch attendee details pre-fill
                      the lead capture form instantly.
                    </p>
                    <div className="screenshot-frame screenshot-frame--wide">
                      <img
                        src={imgBadgeScan}
                        alt="Badge scanning flow: empty form, QR code scan, pre-filled form"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Seamless Experience */}
              {activeTab === 'seamless-experience' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Build comprehensive participant profiles over time without
                    creating friction.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Registration Lookups</h4>
                      <p>
                        When a participant registers at one activation, their
                        profile can be quickly looked up at any subsequent
                        activation—making it easy to verify details or track
                        interactions.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Progressive Profiling</h4>
                      <p>
                        Build on previous responses over time for a more
                        complete participant profile without asking the same
                        questions twice.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Connected Journey</h4>
                      <p>
                        Track participants across multiple activations and
                        touchpoints throughout your event.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Quick Check-In</h4>
                      <p>
                        Returning participants experience faster check-ins at
                        subsequent activations.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Smarter Reporting */}
              {activeTab === 'reporting' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Streamline experience without compromising insights.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Capture User Journeys</h4>
                      <p>
                        Track the complete path participants take through your
                        event, from first touchpoint to final interaction.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Total Interactions</h4>
                      <p>
                        The total number of times consumers engage with each
                        activation, including revisits.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Source of Registration</h4>
                      <p>
                        Understand where consumers started their journey and
                        which activations drive the most engagement.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Unique Interactions</h4>
                      <p>
                        Track individual unique engagements vs. repeat visits to
                        understand behavior patterns.
                      </p>
                    </div>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>Real Event Data</h4>
                    <p className="screenshot-caption">
                      Interaction and registration breakdowns across every
                      activation — visualized instantly.
                    </p>
                    <div className="screenshot-grid">
                      <div className="screenshot-frame">
                        <img
                          src={imgReportingPie}
                          alt="Total Interactions and Source of Registration pie charts"
                          loading="lazy"
                        />
                      </div>
                      <div className="screenshot-frame">
                        <img
                          src={imgReportingFull}
                          alt="Full reporting dashboard with survey response breakdowns"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Live Dashboards */}
              {activeTab === 'dashboards' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    View real-time insights on registrations, interactions, and
                    performance metrics.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Total Registrants</h4>
                      <p>
                        All consumers who completed a survey regardless of
                        activation.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Email Opt-ins</h4>
                      <p>
                        Track opt-in rates and qualified leads with full contact
                        information.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Customizable Targets</h4>
                      <p>
                        Set and monitor goals in real-time throughout your
                        event.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Easy Export</h4>
                      <p>
                        Export reports and graphs in your preferred format for
                        further analysis.
                      </p>
                    </div>
                  </div>
                  <div className="dashboard-features">
                    <h4>View Real-Time Insights On:</h4>
                    <ul>
                      <li>
                        Registrations, interactions, pre-surveys, post-surveys
                      </li>
                      <li>Contest and Marketing Opt-ins</li>
                      <li>Survey Response breakdowns</li>
                      <li>Sales Rep & Brand Ambassador Performance</li>
                      <li>Daily registrant trends and patterns</li>
                    </ul>
                  </div>
                  <div className="reporting-note">
                    <h4>Reporting Your Way</h4>
                    <p>
                      Get the insights you need—whether it's daily details or
                      total rollups, we've got you covered.
                    </p>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>Live Dashboard Views</h4>
                    <p className="screenshot-caption">
                      Monitor registrants, opt-in rates, survey scores, and
                      daily trends — all in real time.
                    </p>
                    <div className="screenshot-grid">
                      <div className="screenshot-frame">
                        <img
                          src={imgDashboardGauges}
                          alt="Total Registrants and Email Opt-ins gauges"
                          loading="lazy"
                        />
                      </div>
                      <div className="screenshot-frame">
                        <img
                          src={imgDashboardGoals}
                          alt="Capability and passion score charts with daily registrant trends"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Integrations */}
              {activeTab === 'integrations' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Built to connect with your existing systems and workflows.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Deliver Raw Data</h4>
                      <p>
                        Receive data in your preferred format—CSV, JSON, XML, or
                        custom specifications.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>API Connections</h4>
                      <p>
                        Connect directly to your APIs for real-time data
                        synchronization.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>CRM Sync</h4>
                      <p>
                        Seamlessly sync with your CRM or database for immediate
                        lead follow-up.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Personalized Outreach</h4>
                      <p>
                        Trigger personalized emails or SMS based on survey
                        responses and expressed interests.
                      </p>
                    </div>
                  </div>
                  <div className="integration-example">
                    <h4>Individual Outreach Example:</h4>
                    <div className="email-example">
                      <p>
                        <em>
                          "Hi John, Thanks for spending time with [Brand] at
                          [Event] yesterday — we hope you enjoyed your test
                          drive experience! You expressed interest in our
                          [Product Interest], and we're here to help you move
                          forward. As a thank you, we're offering an exclusive
                          show promo: $50 off your next purchase!"
                        </em>
                      </p>
                      <p>
                        <strong>
                          Personalized communications based on survey responses
                        </strong>
                      </p>
                    </div>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>Triggered SMS in the Wild</h4>
                    <p className="screenshot-caption">
                      Attendees receive personalized product info and event
                      photos seconds after their interaction.
                    </p>
                    <div className="screenshot-frame screenshot-frame--phone">
                      <img
                        src={imgSmsOutreach}
                        alt="Triggered SMS messages sent to attendees after event interaction"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* iOS Native App */}
              {activeTab === 'ios-app' && (
                <div className="capability-content">
                  <p className="capability-intro">
                    Powerful mobile solution for on-the-ground teams at events.
                  </p>
                  <div className="features-grid">
                    <div className="feature">
                      <h4>Works On or Offline*</h4>
                      <p>
                        Securely capture data even without internet
                        connectivity, syncing when connection returns.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Multi-Language Capabilities</h4>
                      <p>
                        Support for multiple languages to accommodate diverse
                        event audiences.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Team Performance Tracking</h4>
                      <p>
                        Monitor onsite team member performance including Sales
                        Reps and Product Specialists.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Customized Data Capture Forms</h4>
                      <p>
                        Fully customizable forms tailored to your specific data
                        collection needs.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Triggered Communications</h4>
                      <p>
                        Send automated email or SMS with content relevant to
                        consumer interests.
                      </p>
                    </div>
                    <div className="feature">
                      <h4>Real-Time Leaderboards</h4>
                      <p>
                        Motivate teams with live performance rankings and
                        metrics.
                      </p>
                    </div>
                  </div>
                  <div className="capability-screenshot-showcase">
                    <h4>On a Tablet, On the Floor</h4>
                    <p className="screenshot-caption">
                      Badge scanning and capture running in the native app,
                      branded to the client.
                    </p>
                    <div className="screenshot-frame screenshot-frame--phone">
                      <img
                        src={imgBadgeScanIpad}
                        alt="Ford-branded badge scan capture form running on an iPad"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="performance-example">
                    <h4>Team Performance Example:</h4>
                    <div className="leaderboard">
                      <div className="leaderboard-item">
                        <span>Kristina Samuels</span>
                        <span className="score">102</span>
                      </div>
                      <div className="leaderboard-item">
                        <span>Jenny Werner</span>
                        <span className="score">83</span>
                      </div>
                      <div className="leaderboard-item">
                        <span>Francois Delaney</span>
                        <span className="score">82</span>
                      </div>
                      <div className="leaderboard-item">
                        <span>Daniel Smith</span>
                        <span className="score">79</span>
                      </div>
                    </div>
                    <p className="note">
                      *Some features limited in offline mode
                    </p>
                  </div>
                </div>
              )}

              {/* Eight dots close every panel: a visible floor to the section,
                  so the end of a capability reads as an end rather than as
                  more scrolling, and a way to move on without going back up to
                  the picker. */}
              <nav className="capability-dots" aria-label="Capabilities">
                {capabilityCards.map((cap, i) => {
                  const selected = activeTab === cap.id;
                  return (
                    <button
                      key={cap.id}
                      type="button"
                      aria-current={selected ? 'true' : undefined}
                      className={`capability-dot ${selected ? 'active' : ''}`}
                      onClick={() => selectFromDots(cap.id)}
                    >
                      {/* Same two digits as the card up in the picker and the
                          badge on the panel head, so the three agree. */}
                      <span aria-hidden="true">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="visually-hidden">
                        {`${cap.label} (${i + 1} of ${capabilityCards.length})`}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-activation lookup: the differentiator, given its own moment */}
      <section id="lookup" className="lookup-band">
        <div className="container">
          <div className="lookup-inner">
            <div className="lookup-copy">
              <p className="eyebrow">Register once</p>
              <h2 className="lookup-title">One profile, every activation</h2>
              <p className="lookup-lede">
                Anyone who registers at one touchpoint is already known at the
                next. Scan a code or search a name and their details come
                straight back, so nobody fills in the same form twice, however
                many activations you are running.
              </p>
              <ul className="lookup-points">
                <li>
                  Pre-registered online or signed up on the floor, it is the
                  same profile either way
                </li>
                <li>
                  Found by badge scan, QR code or a quick name lookup in seconds
                </li>
                <li>
                  Progressive profiling adds new answers instead of re-asking
                  old ones
                </li>
                <li>
                  Shorter queues, cleaner data, and a journey you can follow
                  across the whole program
                </li>
              </ul>
            </div>
            <div className="lookup-media">
              <div className="screenshot-frame screenshot-frame--wide">
                <img
                  src={imgBadgeScan}
                  alt="An empty capture form, a QR code scan, then the same form already filled in"
                  width={1400}
                  height={576}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work teaser */}
      <section id="work" className="work-teaser">
        <div className="container">
          <h2 className="section-title">What It Looks Like in the Field</h2>
          <p className="features-intro">Three programs we built and ran</p>
          <div className="work-grid">
            {caseStudies.map((study) => (
              <Link
                to={`/work#${study.id}`}
                className="work-card"
                key={study.id}
              >
                <div className="work-card-media">
                  <img
                    src={study.hero.src}
                    alt={study.hero.alt}
                    width={study.hero.width}
                    height={study.hero.height}
                    loading="lazy"
                  />
                </div>
                <div className="work-card-body">
                  <p className="eyebrow">{study.client}</p>
                  <h3 className="work-card-title">{study.title}</h3>
                  <p className="work-card-desc">{study.tagline}</p>
                  <span className="card-cta">View case study →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement lifecycle */}
      <section id="lifecycle" className="lifecycle">
        <div className="container">
          <h2 className="section-title">How an Activation Runs End to End</h2>
          <p className="features-intro">
            Eight stages, one continuous record of every person who engaged
          </p>
          <p className="section-note">
            Every program is built around your goals, so the stages and their
            order vary.
          </p>
          <ol className="lifecycle-grid">
            {lifecycle.map((stage) => (
              <li className="lifecycle-step" key={stage.label}>
                <h3 className="lifecycle-label">{stage.label}</h3>
                <p className="lifecycle-desc">{stage.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="cta-band">
        <div className="container">
          <h2>Let's talk about your next activation</h2>
          <p>
            Tell us what you're planning: the venue, the audience, and what you
            need to walk away with. We'll show you how we'd capture it.
          </p>
          <Link to="/contact" className="cta-button">
            Get in touch
          </Link>
        </div>
      </section>
    </>
  );
}

export default Home;
